import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

import { connectionsApi } from '../../../lib/api/connections';
import { sendOAuthResultToParent } from '../../../lib/connector-oauth';

import { Button } from '../../../components/ui/button';

type CallbackStatus = 'processing' | 'success' | 'error';

/**
 * OAuth Callback Handler for Connections
 *
 * Receives OAuth callback from provider (e.g., Google, Shopify) with code and state,
 * then exchanges the code for tokens via backend API.
 *
 * Always sends result via all communication channels (postMessage, localStorage,
 * BroadcastChannel) regardless of popup detection. This ensures cross-origin
 * OAuth redirects (e.g., Shopify) work even when window.opener is lost.
 * After sending, tries window.close(). Falls back to navigation after a delay.
 */
export default function ConnectionOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Prevent double execution in React StrictMode
  const exchangeInitiated = useRef(false);

  // OAuth callback params - can come from OAuth provider (code/state) or backend redirect (status/connectionId)
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  // Backend redirect params (when backend handles the exchange)
  const backendStatus = searchParams.get('status');
  const backendConnectionId = searchParams.get('connectionId');
  const backendConnectorId = searchParams.get('connectorId');
  const backendReauthorized = searchParams.get('reauthorized') === 'true';
  const backendMessage = searchParams.get('message');

  // GitHub/GitLab OAuth redirect params
  const githubConnected = searchParams.get('github_connected');
  const gitlabConnected = searchParams.get('gitlab_connected');
  const githubError = searchParams.get('github_error');
  const gitlabError = searchParams.get('gitlab_error');

  useEffect(() => {
    // Prevent double execution
    if (exchangeInitiated.current) return;
    exchangeInitiated.current = true;

    const exchangeCode = async () => {
      // Handle GitHub/GitLab OAuth result (backend redirects here after OAuth)
      if (githubConnected || gitlabConnected) {
        const connectionId = githubConnected || gitlabConnected!;
        setStatus('success');
        sendOAuthResultToParent({ success: true, connectionId });
        return;
      }

      if (githubError || gitlabError) {
        const message = githubError || gitlabError!;
        setStatus('error');
        setErrorMessage(message);
        sendOAuthResultToParent({ success: false, error: message });
        return;
      }

      // Handle OAuth provider errors
      if (error) {
        const message = errorDescription || error;
        setStatus('error');
        setErrorMessage(message);
        sendOAuthResultToParent({ success: false, error: message });
        return;
      }

      // Handle backend redirect (when backend already exchanged the code)
      if (backendStatus === 'success' && backendConnectionId) {
        setStatus('success');
        sendOAuthResultToParent({
          success: true,
          connectionId: backendConnectionId,
          connectorId: backendConnectorId || undefined,
          reauthorized: backendReauthorized,
        });
        return;
      }

      if (backendStatus === 'error') {
        const message = backendMessage || 'Authentication failed';
        setStatus('error');
        setErrorMessage(message);
        sendOAuthResultToParent({ success: false, error: message });
        return;
      }

      // Validate required params for frontend exchange flow
      if (!code || !state) {
        const message = 'Missing authorization code or state parameter';
        setStatus('error');
        setErrorMessage(message);
        sendOAuthResultToParent({ success: false, error: message });
        return;
      }

      try {
        // Exchange code for tokens via backend (frontend-initiated exchange)
        const result = await connectionsApi.exchangeOAuthCode(code, state);

        setStatus('success');
        sendOAuthResultToParent({
          success: true,
          connectionId: result.connectionId,
          connectorId: result.connectorId,
          connectorName: result.connectorName,
          reauthorized: result.reauthorized,
        });
      } catch (err: unknown) {
        // Extract error message from axios error or regular error
        let message = 'Failed to exchange authorization code';
        if (err && typeof err === 'object') {
          const axiosError = err as { response?: { data?: { message?: string } }; message?: string };
          message = axiosError.response?.data?.message || axiosError.message || message;
        }

        console.error('OAuth exchange error:', err);
        setStatus('error');
        setErrorMessage(message);
        sendOAuthResultToParent({ success: false, error: message });
      }
    };

    exchangeCode();
  }, [code, state, error, errorDescription, navigate, backendStatus, backendConnectionId, backendConnectorId, backendReauthorized, backendMessage, githubConnected, gitlabConnected, githubError, gitlabError]);

  // Show manual close button if popup doesn't auto-close after success
  const [showManualClose, setShowManualClose] = useState(false);

  useEffect(() => {
    if (status === 'processing') return;

    if (status === 'success') {
      // For success: the parent handles the UX (toast + modal close).
      // If window.close() didn't work, show a manual close button after 2s.
      const timer = setTimeout(() => setShowManualClose(true), 2000);
      return () => clearTimeout(timer);
    }

    // For error: redirect to connectors page after 3s
    const timer = setTimeout(() => {
      toast.error(errorMessage || 'Authentication failed');
      navigate('/dashboard/connectors', { replace: true });
    }, 3000);
    return () => clearTimeout(timer);
  }, [status, navigate, errorMessage]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        {status === 'processing' && (
          <>
            <Loader2 className="h-16 w-16 text-primary mx-auto animate-spin" />
            <h1 className="text-2xl font-semibold text-foreground">
              Connecting...
            </h1>
            <p className="text-muted-foreground">
              Completing authentication
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
            <h1 className="text-2xl font-semibold text-foreground">
              Connection Created!
            </h1>
            <p className="text-muted-foreground">
              This window will close automatically.
            </p>
            {showManualClose && (
              <Button
                variant="outline"
                onClick={() => window.close()}
                className="mt-4"
              >
                Close Window
              </Button>
            )}
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto" />
            <h1 className="text-2xl font-semibold text-foreground">
              Authentication Failed
            </h1>
            <p className="text-muted-foreground">
              {errorMessage || 'An error occurred during authentication'}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Redirecting...
            </p>
          </>
        )}
      </div>
    </div>
  );
}
